import React, { useContext, useEffect } from "react";
import { Typography, Box, Chip } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import { PersonalData } from "../components/PersonalInfo/PersonalData";
import { Positions } from "../components/PersonalInfo/Positions";
import useWindowSize from "../hooks/useWindowSize";
import { MOBILE_WIDTH } from "../App";
import OpsAvatarComponent from "../components/OpsAvatarComponent";
import usePersonalInfo, { UsePersonalInfoReturnType } from "../hooks/usePersonalInfo";
import { DrataIdentifier } from "../components/PersonalInfo/DrataIdentifier";
import { Token } from "../theme";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Button } from "@mui/material";
import { Theme } from "@mui/material/styles";
import OpsDateUtils from "../utils/OpsDateUtils";
import OpsStringUtils from "../utils/OpsStringUtils";

const useStyles = makeStyles<Theme, {}, string>(
  (theme: Theme) =>
    ({
      siteCard: {
        height: "15em",
        margin: "1em",
        border: "none",
        boxShadow: "10px 8px 41px -5px rgba(184,183,183,0.4)",
        justifyContent: "center",
        textAlign: "center",
      },
      siteCardContainer: {
        overflow: "auto",
        height: "18em",
        display: "flex",
        flexWrap: "wrap",
      },
      [theme.breakpoints.down(MOBILE_WIDTH)]: {
        container: {
          display: "flex",
          flexDirection: "column !important",
          rowGap: "16px",
        },
        column: {
          width: "100%",
          maxWidth: "100% !important",
        },
      },
    }) as any,
  { index: 1 },
);

export const PersonalInformationContext = React.createContext({} as any);

export default function PersonalInfo() {
  const contextValue = usePersonalInfo();
  const { isMobileWidth } = useWindowSize();
  const { history, user, isLoading } = useContext<UsePersonalInfoReturnType>(PersonalInformationContext);
  const classes = useStyles();

  useEffect(() => {
    if (contextValue.paramExternalId) {
      contextValue.updateCurrentHistoryEntry(contextValue.paramExternalId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextValue.paramExternalId]);

  const buildHeaderSummary = () => {
    const { user, projectPositions = [] } = contextValue;
    const parts: string[] = [];
    projectPositions.forEach((p) => {
      parts.push(p.shortStartDate ? `${p.projectName} since ${OpsDateUtils.ensureFormatDateUTC(p.shortStartDate)}` : p.projectName);
    });
    if (user?.shortStart) {
      parts.push(`at Ensolvers since ${OpsDateUtils.ensureFormatDateUTC(user.shortStart)}`);
    }
    if (user?.country) {
      parts.push(OpsStringUtils.lowerStrUpperFirstLetter(user.country));
    }
    return parts.join(" · ");
  };

  return (
    <>
      <PersonalInformationContext.Provider value={contextValue}>
        {contextValue.user && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                padding: "16px",
                background: Token.surfaceCard,
                border: `1px solid ${Token.borderDefault}`,
                borderRadius: "8px",
              }}
            >
              <OpsAvatarComponent
                avatarUrl={contextValue.user.avatarUrl}
                forceGenericAvatar={true}
                sx={{ width: 56, height: 56, cursor: "pointer" }}
                onClick={() => contextValue.setUserAvatarUpdate(contextValue.user)}
              />

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                  <Typography variant="h1" fontWeight={700} fontSize={20} letterSpacing="-0.02em" color={Token.textPrimary} noWrap>
                    {`${contextValue.user.firstName} ${contextValue.user.lastName}`}
                  </Typography>

                  {contextValue.user.primaryRole && (
                    <Chip
                      label={contextValue.user.primaryRole}
                      size="small"
                      sx={{
                        backgroundColor: Token.statusProgressBg,
                        color: Token.statusProgressFg,
                      }}
                    />
                  )}
                </Box>

                <Typography fontSize={12.5} color={Token.textTertiary} noWrap>
                  {buildHeaderSummary()}
                </Typography>
              </Box>

              <Button
                variant="contained"
                disableElevation
                startIcon={<EditOutlinedIcon />}
                onClick={() => contextValue.setUserUpdate(contextValue.user)}
                sx={{ backgroundColor: Token.actionPrimary, color: Token.textOnAction, flexShrink: 0 }}
              >
                Edit profile
              </Button>
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 2fr) minmax(0, 1fr)" },
                gap: 1.5,
                pt: 1.5,
              }}
            >
              <PersonalData />
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", "& > *": { flex: 1 } }}>
                  <Positions projectPositions={contextValue.projectPositions} />
                </Box>
                <DrataIdentifier />
              </Box>
            </Box>
          </>
        )}
      </PersonalInformationContext.Provider>
    </>
  );
}
